import { api } from '../slices/api';

/**
 * The stage a job is waiting in. Exhaustive and mutually exclusive -- every
 * undelivered line item is in exactly one, decided server-side.
 */
export type BottleneckBucket =
	| 'new'
	| 'material_selected'
	| 'program_review'
	| 'waiting_on_material'
	| 'waiting_on_kitting'
	| 'ready_to_kit_and_cut'
	| 'waiting_on_cutting'
	| 'ready_to_machine'
	| 'machining'
	| 'done'
	| 'uncategorized'
	| 'delivered';

/** Bars also carry the gap to the ship date, which is not a stage. */
export type BottleneckSegmentKey = BottleneckBucket | 'remaining';

export interface BottleneckSegment {
	bucket: BottleneckSegmentKey;
	seconds: number;
	/** The stay the job is still in, measured up to the moment of the request. */
	open: boolean;
	/** Only on `remaining`: the ship date has passed, so the gap runs backwards. */
	overdue?: boolean;
}

export interface BottleneckRow {
	id: string;
	order_name: string;
	name: string;
	bucket: BottleneckBucket;
	/** Parts still on the job, with cancellations already taken off. */
	quantity: number;
	required_part_ship_date: string | null;
	shop_due_date?: string | null;
	is_overdue: boolean;
	current_dwell_seconds: number;
	/** In order: one entry per stay, then `remaining` unless delivered. */
	segments: BottleneckSegment[];
	/**
	 * Unresolved shop-floor tickets against this job. The stage is unaffected
	 * -- a ticketed job still sits where its statuses put it -- so this is the
	 * board's only way to say "this one is not actually workable".
	 */
	open_ticket_count: number;
	/** Their shop-floor ids, e.g. `FLM-014`, for the row tooltip. */
	open_ticket_uids: string[];
	/** Omitted entirely for viewers without revenue access. */
	revenue?: string;
}

/**
 * Spread of completed stays in this bucket. Null throughout when nothing has
 * left it yet -- an unmeasured bucket, not a fast one.
 */
export interface BottleneckQuartiles {
	p25_seconds: number | null;
	p50_seconds: number | null;
	p75_seconds: number | null;
	p100_seconds: number | null;
	sample_count: number;
}

/**
 * What the quartiles measure. Delivered work never leaves Delivered, so that
 * one section reports lead time -- creation to handoff -- instead of a stay.
 */
export type BottleneckQuartilesBasis = 'stay' | 'lead_time';

export interface BottleneckSection {
	key: BottleneckBucket;
	label: string;
	line_item_count: number;
	/**
	 * How many of those line items have at least one open ticket. Jobs, not
	 * tickets: two tickets on one job is still one job that cannot be worked.
	 */
	ticketed_line_item_count: number;
	quantity: number;
	revenue: string | null;
	quartiles_basis: BottleneckQuartilesBasis;
	quartiles: BottleneckQuartiles;
	rows: BottleneckRow[];
}

export interface BottleneckBoardResponse {
	generated_at: string;
	include_revenue: boolean;
	/** Empty means the board covers every production method. */
	production_methods: string[];
	/** How many of the most recent delivered jobs the last section holds. */
	delivered_limit: number;
	/** Ceiling on live rows; a guard against a data problem, not a page size. */
	live_limit: number;
	/** True when live work hit `live_limit`, so every count below is a floor. */
	live_truncated: boolean;
	sections: BottleneckSection[];
}

export interface BottleneckBoardArgs {
	/** Empty or omitted asks for every method. Anything else is rejected. */
	productionMethods?: string[];
}

export const bottleneckReportApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getBottleneckBoard: builder.query<
			BottleneckBoardResponse,
			BottleneckBoardArgs | void
		>({
			// Filtering is server-side: the counts and quartiles have to be
			// recomputed for the selected methods, not just the rows hidden.
			query: (args) => {
				const methods = args?.productionMethods ?? [];
				return methods.length
					? `orders/reports/bottleneck-board/?production_method=${encodeURIComponent(
							methods.join(',')
					  )}`
					: 'orders/reports/bottleneck-board/';
			},
		}),
	}),
});

export const { useGetBottleneckBoardQuery } = bottleneckReportApi;
